import type { HttpErrorResponse } from '@angular/common/http';

import type { ID } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
  pipeState,
  scopeFactory,
} from '@core/ngrx-legacy';
import {
  AutomationTemplate,
  WorkflowAutomation,
  WorkflowAutomationAdditionalData,
  WorkflowAutomationCreatePayload,
  WorkflowAutomationOptionsData,
  WorkflowAutomationUpdatePayload,
} from '@core/workflow-automations-types';

import { EntityStoreKey } from '../../types';

export const jobIdScope = (jobId: number) => scopeFactory('jobIdScope', jobId);

const idSelector = ({ id }: { id: number }) => id;
const { actions, namespace, reducer, selectors } =
  createEntityCollectionStore<WorkflowAutomation>()(EntityStoreKey.JobWorkflowAutomations, {
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      return {
        fetchJobAutomations: (state, payload: { jobId: number; scope: string }) =>
          adapter.markAsPending(state, { scope: null, entities: [payload.jobId] }),
        fetchJobAutomationsSuccess: (state, { data, scope }) =>
          pipeState(
            state,
            next => adapter.setMany(next, data, { scope }),
            next =>
              adapter.markAsLoaded(next, { scope, entities: data.map(({ id }: { id: ID }) => id) }),
          ),
        fetchJobAutomationsFailed: defaultActions.fetchOneFailed,
        createOnePessimistic: (
          state,
          payload: {
            jobId: number;
            data: WorkflowAutomationCreatePayload;
            scope: string;
            correlationId: string;
          },
        ) => adapter.markAsPending(state),
        updateOnePessimistic: (
          state,
          payload: {
            jobId: number;
            data: WorkflowAutomationUpdatePayload;
            scope: string;
            correlationId: string;
          },
        ) => adapter.markAsPending(state),
        removeOnePessimistic: (state, payload: { data: number; jobId: number }) =>
          defaultActions.removeOnePessimistic(state, payload),
        duplicate: (
          state,
          payload: {
            data: number;
            additionalData: WorkflowAutomationAdditionalData;
            scope: string;
          },
        ) => adapter.markAsPending(state),
        duplicateSuccess: (state, payload: { data: WorkflowAutomation; scope: string }) =>
          pipeState(
            state,
            next => adapter.addOne(next, payload.data, { scope: payload.scope }),
            next => adapter.markAsLoaded(next),
          ),
        duplicateFailed: defaultActions.createOnePessimisticFailed,
        fetchOptions: ngrxEvent<{ id: number }>(),
        fetchOptionsSuccess: ngrxEvent<WorkflowAutomationOptionsData>(),
        fetchOptionsFailed: ngrxEvent<{ id: number; error: HttpErrorResponse }>(),
        fetchAutomationTemplates: ngrxEvent<{ id: number }>(),
        fetchAutomationTemplatesSuccess: ngrxEvent<AutomationTemplate[]>(),
        fetchAutomationTemplatesFailed: ngrxEvent<{ id: number; error: HttpErrorResponse }>(),
      };
    },
  });

const JobWorkflowAutomationsActions = {
  fetchJobAutomations: actions.fetchJobAutomations,
  fetchJobAutomationsSuccess: actions.fetchJobAutomationsSuccess,
  fetchJobAutomationsFailed: actions.fetchJobAutomationsFailed,

  createOnePessimistic: actions.createOnePessimistic,
  createOnePessimisticSuccess: actions.createOnePessimisticSuccess,
  createOnePessimisticFailed: actions.createOnePessimisticFailed,

  updateOnePessimistic: actions.updateOnePessimistic,
  updateOnePessimisticSuccess: actions.updateOnePessimisticSuccess,
  updateOnePessimisticFailed: actions.updateOnePessimisticFailed,

  removeOnePessimistic: actions.removeOnePessimistic,
  removeOnePessimisticFailed: actions.removeOnePessimisticFailed,
  removeOnePessimisticSuccess: actions.removeOnePessimisticSuccess,

  duplicate: actions.duplicate,
  duplicateSuccess: actions.duplicateSuccess,
  duplicateFailed: actions.duplicateFailed,

  fetchOptions: actions.fetchOptions,
  fetchOptionsSuccess: actions.fetchOptionsSuccess,
  fetchOptionsFailed: actions.fetchOptionsFailed,

  fetchAutomationTemplates: actions.fetchAutomationTemplates,
  fetchAutomationTemplatesSuccess: actions.fetchAutomationTemplatesSuccess,
  fetchAutomationTemplatesFailed: actions.fetchAutomationTemplatesFailed,
};

export {
  JobWorkflowAutomationsActions,
  namespace as JobWorkflowAutomationsNamespace,
  reducer as JobWorkflowAutomationsReducer,
  selectors as JobWorkflowAutomationsSelectors,
};
